import { TaskSubTicketPermission } from 'dy-frontend-permissions/lib/permission';

export interface CapabilityPermissions {
    isRoot: boolean;
    has: (permission: string) => boolean;
}

export interface ResolveCapabilitiesParams {
    permissions: CapabilityPermissions;

    // Parent Request is paused, delivered or archived
    isParentClosed: boolean;

    // The caller owns this sub-ticket
    isAssignee: boolean;

    isArchived: boolean;

    // A timer is already open on THIS sub-ticket, owned by the caller.
    //
    // This must be derived from the authoritative global session store, never from a list row's
    // `active_session` snapshot: the row is only as fresh as the last successful list read, and
    // Stop & Save is the one control that has to appear the instant a Start succeeds and has to
    // survive a failed refresh and a parent that closes underneath it.
    hasRunningTimerHere: boolean;
}

export interface SubTicketCapabilities {
    canCreate: boolean;
    canUpdate: boolean;
    canManageAssignee: boolean;
    canManageStatus: boolean;
    canArchive: boolean;
    canRestore: boolean;
    canLogWork: boolean;
    canStartTimer: boolean;
    canStopThenStartHere: boolean;
    canStopTimer: boolean;
}

// Every capability is derived independently, because the permissions genuinely are independent:
// a lead may hold UPDATE without ASSIGNEE_MANAGE and must still be able to rewrite a scope, and
// a designer holding only `status.manage.mod.assigned` may move their own phase and no one
// else's. Deriving one from another is how a permission silently widens.
//
// A closed parent hides new writes rather than disabling them — the established isTaskDelivered
// idiom — with exactly one exception. A timer already running on this sub-ticket may still be
// stopped: the designer must be able to bank work they already did, and nothing auto-stops it
// for them.
export const resolveSubTicketCapabilities = ({
    permissions,
    isParentClosed,
    isAssignee,
    isArchived,
    hasRunningTimerHere,
}: ResolveCapabilitiesParams): SubTicketCapabilities => {
    const isRoot = permissions.isRoot;

    // Writable only while the parent is open and the phase itself is not archived
    const isWritable = !isParentClosed && !isArchived;

    const hasStatusManage = isRoot || permissions.has(TaskSubTicketPermission.STATUS_MANAGE);
    const hasStatusManageAssigned = permissions.has(TaskSubTicketPermission.STATUS_MANAGE_MOD_ASSIGNED);

    return {
        canCreate: (isRoot || permissions.has(TaskSubTicketPermission.CREATE)) && !isParentClosed,

        canUpdate: (isRoot || permissions.has(TaskSubTicketPermission.UPDATE)) && isWritable,

        // Independent of UPDATE: gates both the Designer field and the reassignment request
        canManageAssignee: (isRoot || permissions.has(TaskSubTicketPermission.ASSIGNEE_MANAGE)) && isWritable,

        // Management override applies to every eligible phase; the assignee-scoped variant
        // applies to the caller's own phases only
        canManageStatus: isWritable && (hasStatusManage || (hasStatusManageAssigned && isAssignee)),

        canArchive: (isRoot || permissions.has(TaskSubTicketPermission.ARCHIVE)) && isWritable,

        canRestore:
            (isRoot || permissions.has(TaskSubTicketPermission.RESTORE)) && !isParentClosed && isArchived,

        // Reporting work manually against a phase. Deliberately the SAME rule the status control
        // uses — assigned designer, or the management override — because the backend gates the
        // attributed entry on exactly `manageStatus`. Deriving it from anything else would let
        // the button appear for someone the endpoint then refuses.
        //
        // Kept as its own field rather than an alias of `canManageStatus`: they answer different
        // questions and will diverge the moment either rule moves.
        canLogWork: isWritable && (hasStatusManage || (hasStatusManageAssigned && isAssignee)),

        // The timer is never permission-gated — it is an identity check, which is what
        // structurally stops a manager operating someone else's timer
        canStartTimer: isAssignee && isWritable,

        canStopThenStartHere: isAssignee && isWritable,

        // The one control that survives a closed parent
        canStopTimer: isAssignee && hasRunningTimerHere,
    };
};
